<template>
    <div class="container-fluid">
        <!-- Custom Tab Navigation -->
        <ul class="nav nav-tabs custom-tabs" role="tablist">
            <li class="nav-item" v-for="(value, key, index) in courseData" :key="index">
                <a 
                    class="nav-link" 
                    :class="{ active: selectedTab === index }"
                    @click="selectedTab = index"
                    href="#"
                    role="tab"
                >
                    {{ formatTabName(key) }}
                </a>
            </li>
        </ul>

        <!-- Tab Content -->
        <div class="tab-content mt-3">
            <div 
                v-for="(value, key, index) in courseData" 
                :key="index"
                class="tab-pane"
                :class="{ active: selectedTab === index }"
            >
                <div v-if="Array.isArray(value) && value.length">
                    <!-- Search and Page Size Controls -->
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <div class="input-group">
                                <input 
                                    v-model="searchQuery" 
                                    type="text" 
                                    class="form-control" 
                                    placeholder="Search..."
                                />
                            </div>
                        </div>
                        <div class="col-md-6 text-right">
                            <select v-model="perPage" class="form-control w-50 d-inline-block">
                                <option v-for="option in pageSizeOptions" :key="option" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Table Container -->
                    <div style="overflow-x: auto;">
                        <table class="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th 
                                        v-for="header in getHeaders(value)" 
                                        :key="header.key"
                                        class="sortable-header"
                                        :class="header.thClass"
                                        :style="header.thStyle"
                                        @click="header.sortable ? sortTable(header.key) : null"
                                    >
                                        {{ header.label }}
                                        <span v-if="header.sortable" class="sort-icons">
                                            <i v-if="sortBy === header.key && !sortDesc" class="fas fa-sort-up text-primary"></i>
                                            <i v-else-if="sortBy === header.key && sortDesc" class="fas fa-sort-down text-primary"></i>
                                            <i v-else class="fas fa-sort text-muted"></i>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr 
                                    v-for="(item, rowIndex) in paginatedAndFilteredData(value)" 
                                    :key="rowIndex"
                                    @click="openModal(key, item)"
                                    :class="getRowClass(item)"
                                    style="cursor: pointer;"
                                >
                                    <td v-for="header,index in getHeaders(value)" :key="index">
                                        <button 
                                            v-if="header.key === 'enrollment'" 
                                            class="btn btn-danger btn-sm"
                                            @click.stop="enrolledUser(item)"
                                        >
                                            Enroll Course
                                        </button>
                                        <span v-else @click.stop="copyToClipboard(item[header.key])">
                                            {{ item[header.key] }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Custom Pagination -->
                    <nav aria-label="Table pagination">
                        <ul class="pagination pagination-sm justify-content-center">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <a class="page-link" @click="currentPage = Math.max(1, currentPage - 1)" href="#">
                                    Previous
                                </a>
                            </li>
                            <li 
                                v-for="page in totalPages" 
                                :key="page"
                                class="page-item"
                                :class="{ active: currentPage === page }"
                            >
                                <a class="page-link" @click="currentPage = page" href="#">{{ page }}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <a class="page-link" @click="currentPage = Math.min(totalPages, currentPage + 1)" href="#">
                                    Next
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>

                <div v-else-if="value && typeof value === 'object'">
                    <pre>{{ value }}</pre>
                </div>

                <div v-else>
                    <p class="text-center text-muted">No Data Available</p>
                </div>
            </div>
        </div>

        <!-- Course Modal -->
        <CourseModal 
            :course="selectedCourse" 
            :isOpen="localCourseModalState" 
            @update:isOpen="syncModalState" 
            @close="closeModal" 
            :tenantId="tenantId" 
        />
    </div>
</template>

<script>
import CourseModal from './CourseModal.vue';
import {EnrollInCourse} from "../../Helper/UserApiHelper";

export default {
    name: "CourseSupportDataTable",
    props: {
        courseData: {
            type: Object,
            required: true
        },
        tenantId: String
    },
    components: {
        CourseModal
    },
    data() {
        return {
            searchQuery: "", 
            currentPage: 1, 
            perPage: 10,    
            pageSizeOptions: [5, 10, 20, 50, 100],
            selectedCourse: null,
            localCourseModalState: false,
            selectedTab: 0,
            sortBy: null,
            sortDesc: false
        };
    },
    computed: {
        activeTabKey() {
            return Object.keys(this.courseData)[this.selectedTab] || "";
        },
        totalPages() {
            const activeData = Object.values(this.courseData)[this.selectedTab];
            if (!Array.isArray(activeData)) return 1;
            const filteredData = this.filterData(activeData);
            return Math.ceil(filteredData.length / this.perPage);
        }
    },
    methods: {
        formatTabName(key) {
            return key
                .replace(/([A-Z])/g, " $1") 
                .replace(/[_-]/g, " ") 
                .replace(/\b\w/g, char => char.toUpperCase()) 
                .trim();
        },
        getHeaders(items) {
            if (!Array.isArray(items) || items.length === 0) return [];
            let headers = Object.keys(items[0]).map(key => ({
                key,
                label: this.formatTabName(key),
                sortable: true,
                thClass: 'sortable-header',
                thStyle: {
                    whiteSpace: 'nowrap',
                    minWidth: 'max-content'
                }
            }));
            if(this.activeTabKey.toLowerCase() === "productvariantdetails"){
                headers.push({
                    key: "enrollment",
                    label: "Enroll Course",
                    sortable: false,
                    thClass: 'text-center',
                    thStyle: {whiteSpace: 'nowrap', minWidth: 'max-content'}
                });
            }
            return headers;
        },
        async enrolledUser(productVariant){

            let productVariantId = productVariant.course.split(" - ")[0];

            if(!productVariantId){
                alert("Product Variant ID not found!");
                return;
            }
            if (!confirm("Are you sure you want to enroll in this course?")) return;
            try{
                let enrollCourseData = {
                    productVariantID: productVariantId,
                    tenantId: this.tenantId
                };
                let response = await EnrollInCourse(enrollCourseData);
                if (response?.message) {
                    this.$toasted.success(response.message, {
                        theme: "outline",
                        position: "bottom-center",
                        duration: 3000,
                    });
                } 
            }catch(err){
                this.$toasted.error("Error in enrolling course.", {
                    icon: "exclamation-triangle",
                    theme: "outline",
                    position: "bottom-center",
                    singleton: true,
                    duration: 3000,
                });
            }
        },
        getRowClass(item) {
            // Check if item has isProductVariantDeleted: true
            if (item.isProductVariantDeleted === true || item.isUserSubscriptionDeleted === true || item.isSubjectDeleted === true) {
                return 'table-danger'; // Bootstrap class for red background
            }
            return '';
        },
        filterData(data) {
            if (!this.searchQuery) return data;
            return data.filter(item => {
                return Object.values(item).some(value => 
                    String(value).toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            });
        },
        sortTable(key) {
            if (this.sortBy === key) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = key;
                this.sortDesc = false;
            }
        },
        paginatedAndFilteredData(data) {
            let filteredData = this.filterData(data);
            
            // Apply sorting
            if (this.sortBy) {
                filteredData.sort((a, b) => {
                    let aVal = a[this.sortBy];
                    let bVal = b[this.sortBy];
                    
                    if (aVal < bVal) return this.sortDesc ? 1 : -1;
                    if (aVal > bVal) return this.sortDesc ? -1 : 1;
                    return 0;
                });
            }
            
            // Apply pagination
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return filteredData.slice(start, end);
        },
        pageChanged(page) {
            this.currentPage = page;
        },
        copyToClipboard(value) {
            if (!value) return;

            navigator.clipboard.writeText(value.toString())
                .then(() => {
                    this.$toasted.success(
                        `${value} copied to clipboard.`, {
                        theme: "outline",
                        position: "bottom-center",
                        duration: 3000,
                    });
                })
                .catch(err => {
                    this.$toasted.error(
                        "Failed to copy.", {
                        icon: "exclamation-triangle",
                        theme: "outline",
                        position: "bottom-center",
                        singleton: true,
                        duration: 3000,
                    });
                    console.error("Failed to copy: ", err);
                });
        },
        openModal(tabKey, row) {
            console.log("Tab Key:", tabKey, "Row Data:", row);
            if (tabKey === "courseEnrollmentDetails") {
                this.selectedCourse = row;
                this.localCourseModalState = true;
            }
        },
        syncModalState(newState) {
            this.localCourseModalState = newState;
        },
        closeModal() {
            this.localCourseModalState = false;
        }
    },
    watch: {
        selectedTab() {
            // Reset pagination when switching tabs
            this.currentPage = 1;
            this.sortBy = null;
            this.sortDesc = false;
        }
    }
};
</script>

<style scoped>
.custom-tabs {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: thin;
    scrollbar-color: #bbb transparent;
}

.custom-tabs::-webkit-scrollbar {
    height: 5px;
}

.custom-tabs::-webkit-scrollbar-thumb {
    background: #bbb;
    border-radius: 10px;
}

.sort-icons {
    margin-left: 5px;
    font-size: 0.8em;
}

.sortable-header {
    cursor: pointer;
    user-select: none;
    white-space: nowrap !important;
}

.sortable-header:hover {
    background-color: #f8f9fa;
}

.sortable-header:hover .sort-icons .fa-sort {
    color: #007bff !important;
}

.table th {
    white-space: nowrap !important;
    vertical-align: middle;
}

.table th,
.table td {
    padding: 0.75rem 0.5rem;
}

/* Red background for deleted product variants */
.table-danger {
    background-color: #f8d7da !important;
}

.table-danger:hover {
    background-color: #f5c6cb !important;
}

.nav-link {
    cursor: pointer;
}

/* Copy to clipboard cursor */
.table td span {
    cursor: pointer;
}

.table td span:hover {
    background-color: #e9ecef;
    border-radius: 3px;
    padding: 2px 4px;
}
</style>